import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { gsap } from "gsap";
import { IDestroyOptions, Point, Sprite, TextStyle } from "pixi.js";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { IRenterPolicyModel } from "../../../common/models/Renters/IRenterPolicyModel";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { RenterQuestIntroResourcePackage } from "../resources/RenterQuestIntroResourcePackage";
import { ButtonWithScaleAnimationAndText } from "../../../common/components/ButtonWithScaleAnimationAndText";
import { GameFont } from "../../../common/GameFont";
import { TextsContainer } from "../components/TextsContainer";

export class RenterQuestIntroPopupView extends BaseScalableView<void> {
    private static readonly GOT_IT_TEXT = "GOT IT!";

    private readonly renterPolicyModel: IRenterPolicyModel;
    private readonly commonResources: CommonResourcesPackage;
    private readonly renterIntroResources: RenterQuestIntroResourcePackage;

    private viewBackground: Sprite;
    private background: Sprite;
    private logo: Sprite;
    private renterIcon: Sprite;
    private hand: Sprite;
    private textsContainer: TextsContainer;
    private gotItButton: ButtonWithScaleAnimationAndText;
    private showViewAnimationTimeLine: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        canvasScaler: ICanvasScaler,
        renterPolicyModel: IRenterPolicyModel,
        commonResources: CommonResourcesPackage,
        renterIntroResources: RenterQuestIntroResourcePackage
    ) {
        super(operationControls, canvasScaler);
        this.renterPolicyModel = renterPolicyModel;
        this.commonResources = commonResources;
        this.renterIntroResources = renterIntroResources;
        this.showViewAnimationTimeLine = new gsap.core.Timeline({ paused: true });
        this.showViewAnimationTimeLine.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.4, ease: "power1.in" });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_lobby_jpg);
        this.viewBackground.eventMode = "static";
        this.background = new Sprite(this.renterIntroResources.assets_views_renter_quest_intro_background_png);
        this.logo = new Sprite(this.renterIntroResources.assets_views_renter_quest_intro_logo_png);

        this.renterIcon = new Sprite(this.renterIntroResources.assets_views_renter_quest_intro_renter_icon_png);
        this.hand = new Sprite(this.commonResources.assets_common_ui_hand_hand_png);
        this.hand.scale.set(-0.6, 0.6);
        this.hand.angle = -73;

        this.textsContainer = new TextsContainer(this.renterPolicyModel.configuration.value.requiredUpgradePoints, this.renterPolicyModel.configuration.value.durationInHours);

        this.gotItButton = new ButtonWithScaleAnimationAndText(
            this.commonResources.assets_common_ui_button_green_png,
            RenterQuestIntroPopupView.GOT_IT_TEXT,
            new ButtonTextStyle()
        );
        this.gotItButton.width = 300;
        this.gotItButton.height = 154;
        this.gotItButton.buttonTextPosition = new Point(90, 40);
        this.gotItButton.onUp.add(this.gotItClickHandle, this);

        this.addChild(
            this.viewBackground,
            this.background,
            this.logo,
            this.renterIcon,
            this.textsContainer,
            this.hand,
            this.gotItButton
        );
    }

    private async gotItClickHandle(): Promise<void> {
        await this.hide();
        this.operationControls.complete();
    }

    private async show(): Promise<void> {
        await this.showViewAnimationTimeLine.play();
    }

    private async hide(): Promise<void> {
        this.gotItButton.enabled = false;
        await this.showViewAnimationTimeLine.reverse();
    }

    private layout(_width: number, _height: number): void {
        this.logo.position.set(158, 106);
        this.renterIcon.position.set(577, 420);
        this.hand.position.set(360, 460);
        this.gotItButton.position.set(267, 1512);
        this.textsContainer.position.set(158, 772);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.gotItButton.onUp.remove(this.gotItClickHandle, this);
        this.showViewAnimationTimeLine?.kill();
        super.destroy(options);
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            dropShadow: true,
            dropShadowAngle: 0.2,
            dropShadowBlur: 5,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 92,
            miterLimit: 0
        });
    }
}
